import { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  MapPin,
  Star,
  Bed,
  Wifi,
  Utensils,
  Snowflake,
  Dumbbell,
  Droplets,
  ParkingCircle,
  Users,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  Luggage,
  ShieldCheck,
  Clock,
  Globe,
  Home,
  Mountain,
  Umbrella,
  Ship,
  Plane,
  Map,
  Compass,
  Wallet,
  Heart
} from 'lucide-react';

// Custom marker icons with different colors based on hostel type
const createCustomIcon = (type) => {
  const colors = {
    standard: '#3b82f6', // Blue
    boutique: '#8b5cf6', // Purple
    eco: '#10b981', // Emerald
    party: '#ef4444', // Red
    luxury: '#f59e0b' // Amber
  };
  
  return L.divIcon({
    html: `
      <div class="relative">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3" fill="${colors[type] || '#3b82f6'}"/>
        </svg>
        <div class="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 rounded-full p-0.5 shadow-xs">
          <Bed class="h-3 w-3 ${colors[type] || '#3b82f6'}" />
        </div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Custom map control component for recentering
const RecenterControl = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, map.getZoom(), {
      duration: 1.5,
      easeLinearity: 0.25
    });
  }, [center, map]);
  return null;
};

const HostelMap = ({ darkMode }) => {
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [priceRange, setPriceRange] = useState([10, 300]);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [amenitiesFilter, setAmenitiesFilter] = useState([]);
  const [hostelTypeFilter, setHostelTypeFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeHostel, setActiveHostel] = useState(null);
  const [mapCenter, setMapCenter] = useState([1.3733, 32.2903]); // Uganda center
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [savedHostels, setSavedHostels] = useState([]);

  // Available amenities with icons
  const amenities = [
    { id: 'wifi', name: 'WiFi', icon: <Wifi size={16} /> },
    { id: 'restaurant', name: 'Restaurant', icon: <Utensils size={16} /> },
    { id: 'ac', name: 'Air Conditioning', icon: <Snowflake size={16} /> },
    { id: 'gym', name: 'Gym', icon: <Dumbbell size={16} /> },
    { id: 'pool', name: 'Pool', icon: <Droplets size={16} /> },
    { id: 'parking', name: 'Parking', icon: <ParkingCircle size={16} /> },
    { id: 'luggage', name: 'Luggage Storage', icon: <Luggage size={16} /> },
    { id: 'security', name: '24/7 Security', icon: <ShieldCheck size={16} /> },
    { id: 'bar', name: 'Bar', icon: <Umbrella size={16} /> },
    { id: 'laundry', name: 'Laundry', icon: <Droplets size={16} /> },
    { id: 'breakfast', name: 'Free Breakfast', icon: <Utensils size={16} /> },
    { id: 'tour', name: 'Tour Desk', icon: <Compass size={16} /> }
  ];

  // Hostel types with icons
  const hostelTypes = [
    { id: 'standard', name: 'Standard', icon: <Home size={16} /> },
    { id: 'boutique', name: 'Boutique', icon: <Star size={16} /> },
    { id: 'eco', name: 'Eco', icon: <Mountain size={16} /> },
    { id: 'party', name: 'Party', icon: <Umbrella size={16} /> },
    { id: 'luxury', name: 'Luxury', icon: <Bed size={16} /> }
  ];

  // Enhanced mock data with more locations and details
  const mockHostels = useMemo(() => [
    // Uganda hostels
    {
      id: 1,
      name: "Kampala Backpackers",
      latitude: 0.3136,
      longitude: 32.5811,
      city: "Kampala",
      country: "Uganda",
      price: 25,
      rating: 4.5,
      type: 'standard',
      amenities: ['wifi', 'restaurant', 'parking', 'bar'],
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945",
      description: "A vibrant hostel in the heart of Kampala with great social spaces and nightly events.",
      reviews: 128,
      distance: "1.2 km from city center"
    },
    {
      id: 2,
      name: "Jinja Nile Hostel",
      latitude: 0.4244,
      longitude: 33.2026,
      city: "Jinja",
      country: "Uganda",
      price: 30,
      rating: 4.8,
      type: 'eco',
      amenities: ['wifi', 'restaurant', 'pool', 'luggage', 'tour'],
      image: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4",
      description: "Overlooking the Nile river, perfect for adventure seekers with rafting packages available.",
      reviews: 96,
      distance: "Riverfront property"
    },
    {
      id: 3,
      name: "Entebbe Lakeside",
      latitude: 0.0644,
      longitude: 32.4465,
      city: "Entebbe",
      country: "Uganda",
      price: 45,
      rating: 4.6,
      type: 'luxury',
      amenities: ['wifi', 'ac', 'restaurant', 'pool', 'gym', 'laundry'],
      image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267",
      description: "Luxury hostel with lake views and premium amenities near the international airport.",
      reviews: 87,
      distance: "5 min from airport"
    },
    // Africa
    {
      id: 4,
      name: "Cape Town Mountain View",
      latitude: -33.9249,
      longitude: 18.4241,
      city: "Cape Town",
      country: "South Africa",
      price: 35,
      rating: 4.9,
      type: 'boutique',
      amenities: ['wifi', 'parking', 'pool', 'security', 'breakfast'],
      image: "https://images.unsplash.com/photo-1516426122078-c23e76319801",
      description: "Stunning views of Table Mountain from our rooftop terrace with designer interiors.",
      reviews: 215,
      distance: "Sea Point district"
    },
    {
      id: 5,
      name: "Marrakech Riad Hostel",
      latitude: 31.6295,
      longitude: -7.9811,
      city: "Marrakech",
      country: "Morocco",
      price: 28,
      rating: 4.7,
      type: 'boutique',
      amenities: ['wifi', 'restaurant', 'pool', 'breakfast', 'tour'],
      image: "https://images.unsplash.com/photo-1518544866330-95b329c56a53",
      description: "Authentic Moroccan riad experience with courtyard pool in the Medina.",
      reviews: 178,
      distance: "Medina center"
    },
    // Europe
    {
      id: 6,
      name: "Berlin Hipster Hostel",
      latitude: 52.5200,
      longitude: 13.4050,
      city: "Berlin",
      country: "Germany",
      price: 45,
      rating: 4.2,
      type: 'party',
      amenities: ['wifi', 'restaurant', 'gym', 'security', 'bar', 'laundry'],
      image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267",
      description: "Trendy hostel in Berlin's creative district with rooftop bar and live music.",
      reviews: 342,
      distance: "Kreuzberg district"
    },
    {
      id: 7,
      name: "Barcelona Beach House",
      latitude: 41.3851,
      longitude: 2.1734,
      city: "Barcelona",
      country: "Spain",
      price: 38,
      rating: 4.8,
      type: 'party',
      amenities: ['wifi', 'bar', 'pool', 'luggage', 'breakfast'],
      image: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4",
      description: "Beachfront party hostel with daily social events and paella nights.",
      reviews: 276,
      distance: "50m from beach"
    },
    // Asia
    {
      id: 8,
      name: "Bangkok Social Hostel",
      latitude: 13.7563,
      longitude: 100.5018,
      city: "Bangkok",
      country: "Thailand",
      price: 18,
      rating: 4.7,
      type: 'standard',
      amenities: ['wifi', 'ac', 'restaurant', 'luggage', 'tour'],
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945",
      description: "Lively hostel with daily social events in downtown Bangkok near Khao San Road.",
      reviews: 432,
      distance: "5 min walk to Khao San"
    },
    {
      id: 9,
      name: "Bali Eco Lodge",
      latitude: -8.3405,
      longitude: 115.0920,
      city: "Ubud",
      country: "Indonesia",
      price: 32,
      rating: 4.9,
      type: 'eco',
      amenities: ['wifi', 'restaurant', 'pool', 'tour', 'laundry'],
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5",
      description: "Sustainable jungle hostel with yoga deck and organic restaurant.",
      reviews: 198,
      distance: "Ubud center"
    },
    // Americas
    {
      id: 10,
      name: "Rio Beach Hostel",
      latitude: -22.9068,
      longitude: -43.1729,
      city: "Rio de Janeiro",
      country: "Brazil",
      price: 29,
      rating: 4.6,
      type: 'party',
      amenities: ['wifi', 'bar', 'pool', 'breakfast', 'tour'],
      image: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325",
      description: "Vibrant hostel just steps from Copacabana beach with samba nights.",
      reviews: 312,
      distance: "Beachfront"
    },
    {
      id: 11,
      name: "New York Loft Hostel",
      latitude: 40.7128,
      longitude: -74.0060,
      city: "New York",
      country: "USA",
      price: 65,
      rating: 4.4,
      type: 'luxury',
      amenities: ['wifi', 'ac', 'gym', 'laundry', 'security'],
      image: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4",
      description: "Industrial-chic loft-style hostel in Brooklyn with skyline views.",
      reviews: 287,
      distance: "Williamsburg"
    },
    // More locations...
    {
      id: 12,
      name: "Sydney Harbour Hostel",
      latitude: -33.8688,
      longitude: 151.2093,
      city: "Sydney",
      country: "Australia",
      price: 42,
      rating: 4.7,
      type: 'boutique',
      amenities: ['wifi', 'restaurant', 'pool', 'bar', 'tour'],
      image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9",
      description: "Designer hostel with Opera House views and rooftop BBQ area.",
      reviews: 154,
      distance: "The Rocks district"
    },
    {
      id: 13,
      name: "Kyoto Zen House",
      latitude: 35.0116,
      longitude: 135.7681,
      city: "Kyoto",
      country: "Japan",
      price: 38,
      rating: 4.9,
      type: 'eco',
      amenities: ['wifi', 'restaurant', 'tour', 'laundry'],
      image: "https://images.unsplash.com/photo-1492571350019-22de08371fd3",
      description: "Traditional Japanese hostel with zen garden and tea ceremonies.",
      reviews: 231,
      distance: "Gion district"
    }
  ], []);

  // Countries with hostel counts
  const countries = useMemo(() => {
    const countryCounts = {};
    mockHostels.forEach(hostel => {
      countryCounts[hostel.country] = (countryCounts[hostel.country] || 0) + 1;
    });
    return Object.entries(countryCounts)
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count);
  }, [mockHostels]);

  // Fetch hostels data (using mock data in this example)
  useEffect(() => {
    const fetchHostels = async () => {
      try {
        setLoading(true);
        // In a real app, you would fetch from your API
        // const response = await axios.get('/api/hostels');
        
        // Simulate API loading delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setHostels(mockHostels);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchHostels();
  }, [mockHostels]);

  // Filter hostels based on selected filters and search
  const filteredHostels = useMemo(() => {
    return mockHostels.filter(hostel => {
      // Country filter
      if (selectedCountry !== 'All' && hostel.country !== selectedCountry) {
        return false;
      }
      
      // Price filter
      if (hostel.price < priceRange[0] || hostel.price > priceRange[1]) {
        return false;
      }
      
      // Rating filter
      if (hostel.rating < ratingFilter) {
        return false;
      }
      
      // Amenities filter
      if (amenitiesFilter.length > 0 && 
          !amenitiesFilter.every(amenity => hostel.amenities.includes(amenity))) {
        return false;
      }
      
      // Hostel type filter
      if (hostelTypeFilter.length > 0 && !hostelTypeFilter.includes(hostel.type)) {
        return false;
      }
      
      // Search query filter
      if (searchQuery && !hostel.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
          !hostel.city.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [mockHostels, selectedCountry, priceRange, ratingFilter, amenitiesFilter, hostelTypeFilter, searchQuery]);

  // Focus on selected country
  const focusOnCountry = useCallback((country) => {
    if (country === 'All') {
      setMapCenter([20, 0]);
      return;
    }
    
    const countryHostels = mockHostels.filter(h => h.country === country);
    if (countryHostels.length > 0) {
      const avgLat = countryHostels.reduce((sum, h) => sum + h.latitude, 0) / countryHostels.length;
      const avgLng = countryHostels.reduce((sum, h) => sum + h.longitude, 0) / countryHostels.length;
      setMapCenter([avgLat, avgLng]);
    }
  }, [mockHostels]);

  // Toggle hostel in saved list
  const toggleSavedHostel = (hostelId) => {
    setSavedHostels(prev => 
      prev.includes(hostelId) 
        ? prev.filter(id => id !== hostelId) 
        : [...prev, hostelId]
    );
  };

  // Toggle filters
  const toggleAmenity = (amenity) => {
    setAmenitiesFilter(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity) 
        : [...prev, amenity]
    );
  };

  const toggleHostelType = (type) => {
    setHostelTypeFilter(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type) 
        : [...prev, type]
    );
  };

  const resetFilters = () => {
    setSelectedCountry('All');
    setPriceRange([10, 300]);
    setRatingFilter(0);
    setAmenitiesFilter([]);
    setHostelTypeFilter([]);
    setSearchQuery('');
    focusOnCountry('All');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-lg">Discovering hostels worldwide...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center p-6 max-w-md mx-auto bg-red-50 dark:bg-red-900/10 rounded-lg">
          <div className="text-red-500 dark:text-red-400 mb-4">
            <X className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-xl font-medium mb-2">Connection Error</h3>
          <p className="mb-4">We couldn't load hostel data. {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen ${darkMode ? 'dark' : ''}`}>
      {/* Header */}
      <header className={`p-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold flex items-center">
            <Globe className="h-5 w-5 mr-2 text-amber-500" />
            <span className="hidden sm:inline">Global Hostel</span> Explorer
          </h1>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
              className={`flex items-center px-3 py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              {viewMode === 'map' ? (
                <>
                  <Map className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">List View</span>
                </>
              ) : (
                <>
                  <Compass className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Map View</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-3 py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <Filter className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Filters</span>
              {showFilters ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="mt-4 relative">
          <input
            type="text"
            placeholder="Search hostels or cities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full p-3 pl-10 rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} border focus:ring-2 focus:ring-amber-500 focus:border-transparent`}
          />
          <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Filter Hostels</h3>
              <button
                onClick={resetFilters}
                className="text-sm text-amber-500 hover:text-amber-600"
              >
                Reset All
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Country Filter */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Country
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    focusOnCountry(e.target.value);
                  }}
                  className={`w-full p-2 rounded-md border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}
                >
                  <option value="All">All Countries ({mockHostels.length})</option>
                  {countries.map(({ country, count }) => (
                    <option key={country} value={country}>
                      {country} ({count})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Price Filter */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Price Range: ${priceRange[0]} - ${priceRange[1]}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="10"
                    max="300"
                    step="5"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="w-full"
                  />
                  <input
                    type="range"
                    min="10"
                    max="300"
                    step="5"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>$10</span>
                  <span>$300+</span>
                </div>
              </div>
              
              {/* Rating Filter */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Minimum Rating: {ratingFilter > 0 ? `${ratingFilter}+` : 'Any'}
                </label>
                <div className="flex items-center space-x-1">
                  {[0, 1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setRatingFilter(rating)}
                      className={`p-1 rounded ${ratingFilter === rating ? 'text-amber-500' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      <Star
                        className={`h-5 w-5 ${ratingFilter >= rating ? 'fill-current' : ''}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Hostel Type Filter */}
            <div className="mt-6">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Hostel Type
              </label>
              <div className="flex flex-wrap gap-2">
                {hostelTypes.map(({ id, name, icon }) => (
                  <button
                    key={id}
                    onClick={() => toggleHostelType(id)}
                    className={`flex items-center px-3 py-1.5 rounded-full text-sm ${hostelTypeFilter.includes(id) 
                      ? 'bg-amber-500 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
                  >
                    <span className="mr-1.5">{icon}</span>
                    {name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Amenities Filter */}
            <div className="mt-6">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Amenities
              </label>
              <div className="flex flex-wrap gap-2">
                {amenities.map(({ id, name, icon }) => (
                  <button
                    key={id}
                    onClick={() => toggleAmenity(id)}
                    className={`flex items-center px-3 py-1 rounded-full text-sm ${amenitiesFilter.includes(id) 
                      ? 'bg-amber-500 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
                  >
                    <span className="mr-1">{icon}</span>
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* Main Content */}
      <div className="flex-1 relative">
        {viewMode === 'map' ? (
          <>
            <MapContainer
              center={mapCenter}
              zoom={selectedCountry === 'All' ? 2 : 6}
              style={{ height: '100%', width: '100%' }}
              className={`${darkMode ? 'dark-map' : ''}`}
            >
              <RecenterControl center={mapCenter} />
              
              {/* Dark/Light mode tiles */}
              {darkMode ? (
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
              ) : (
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
              )}
              
              {/* Hostel Markers */}
              {filteredHostels.map(hostel => (
                <Marker
                  key={hostel.id}
                  position={[hostel.latitude, hostel.longitude]}
                  icon={createCustomIcon(hostel.type)}
                  eventHandlers={{
                    click: () => setActiveHostel(hostel)
                  }}
                >
                  <Popup>
                    <div className="w-64">
                      <div className="flex justify-between items-start">
                        <h3 className="font-bold mb-1">{hostel.name}</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSavedHostel(hostel.id);
                          }}
                          className="p-1 -mt-1 -mr-1"
                        >
                          <Heart
                            className={`h-4 w-4 ${savedHostels.includes(hostel.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                          />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                        <MapPin className="inline h-3 w-3 mr-1" />
                        {hostel.city}, {hostel.country}
                      </p>
                      <div className="flex items-center mb-2">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500 mr-1" />
                        <span className="font-medium">{hostel.rating}</span>
                        <span className="mx-2">•</span>
                        <span className="font-medium">${hostel.price}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/night</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveHostel(hostel);
                        }}
                        className="text-sm text-amber-500 hover:text-amber-600 flex items-center"
                      >
                        View details <ArrowRight className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            
            {/* Hostel Details Sidebar */}
            {activeHostel && (
              <div className={`absolute top-4 right-4 w-80 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-lg shadow-lg z-[1000] ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-bold">{activeHostel.name}</h2>
                    <button
                      onClick={() => setActiveHostel(null)}
                      className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <div className="h-48 w-full bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden mb-3">
                      <img 
                        src={activeHostel.image} 
                        alt={activeHostel.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm">{activeHostel.city}, {activeHostel.country}</span>
                      </div>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500 mr-1" />
                        <span className="font-medium">{activeHostel.rating}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">({activeHostel.reviews})</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center">
                        <Wallet className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span className="font-medium">${activeHostel.price}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/night</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm">{activeHostel.distance}</span>
                      </div>
                    </div>
                    
                    <p className="mb-4 text-sm">{activeHostel.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Amenities</h4>
                      <div className="flex flex-wrap gap-2">
                        {activeHostel.amenities.map(amenityId => {
                          const amenity = amenities.find(a => a.id === amenityId);
                          return amenity ? (
                            <span 
                              key={amenityId}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                            >
                              {amenity.icon}
                              <span className="ml-1">{amenity.name}</span>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                    
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => toggleSavedHostel(activeHostel.id)}
                        className={`flex-1 py-2 rounded-md font-medium flex items-center justify-center ${savedHostels.includes(activeHostel.id) 
                          ? 'bg-gray-200 dark:bg-gray-700 text-amber-500' 
                          : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                      >
                        <Heart 
                          className={`h-4 w-4 mr-2 ${savedHostels.includes(activeHostel.id) ? 'fill-current' : ''}`} 
                        />
                        {savedHostels.includes(activeHostel.id) ? 'Saved' : 'Save'}
                      </button>
                      <button className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-medium">
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* List View */
          <div className={`h-full overflow-y-auto p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredHostels.map(hostel => (
                <div 
                  key={hostel.id}
                  className={`rounded-lg overflow-hidden shadow-md ${darkMode ? 'bg-gray-700' : 'bg-white'} hover:shadow-lg transition-shadow`}
                >
                  <div className="relative">
                    <img 
                      src={hostel.image} 
                      alt={hostel.name} 
                      className="w-full h-48 object-cover"
                    />
                    <button
                      onClick={() => toggleSavedHostel(hostel.id)}
                      className="absolute top-2 right-2 p-2 bg-white/90 dark:bg-gray-800/90 rounded-full"
                    >
                      <Heart
                        className={`h-4 w-4 ${savedHostels.includes(hostel.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                      />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                      ${hostel.price}/night
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold">{hostel.name}</h3>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500 mr-1" />
                        <span>{hostel.rating}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      {hostel.city}, {hostel.country}
                    </p>
                    <p className="text-sm mb-4 line-clamp-2">{hostel.description}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {hostel.amenities.slice(0, 4).map(amenityId => {
                        const amenity = amenities.find(a => a.id === amenityId);
                        return amenity ? (
                          <span 
                            key={amenityId}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {amenity.icon}
                            <span className="ml-1">{amenity.name}</span>
                          </span>
                        ) : null;
                      })}
                      {hostel.amenities.length > 4 && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                          +{hostel.amenities.length - 4}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setActiveHostel(hostel);
                        setViewMode('map');
                        setMapCenter([hostel.latitude, hostel.longitude]);
                      }}
                      className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium flex items-center justify-center"
                    >
                      View on Map <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredHostels.length === 0 && (
              <div className={`flex flex-col items-center justify-center h-64 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} p-6 text-center`}>
                <Compass className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Hostels Found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Try adjusting your filters or search for different locations
                </p>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm"
                >
                  Reset All Filters
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Filter Summary */}
        <div className={`absolute bottom-4 left-4 p-3 rounded-lg shadow-lg z-[1000] ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center">
            <span className="text-sm mr-2">
              Showing <strong>{filteredHostels.length}</strong> {filteredHostels.length === 1 ? 'hostel' : 'hostels'}
            </span>
            {(selectedCountry !== 'All' || priceRange[0] > 10 || priceRange[1] < 300 || ratingFilter > 0 || amenitiesFilter.length > 0 || hostelTypeFilter.length > 0 || searchQuery) && (
              <button
                onClick={resetFilters}
                className="text-xs text-amber-500 hover:text-amber-600 flex items-center"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostelMap;